import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { TextField } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import type { FieldErrors } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';

import { getPblCase, submitPblEvaluation } from '@/api/pbl.api';
import PageContainer from '@/components/Container/PageContainer';
import StarRating from '@/components/Pbl/StarRating';
import { Button } from '@/components/UI/Button';
import useResize from '@/hooks/useResize';
import PATH from '@/router/path';
import type { PblCase, PblMeeting } from '@/types/pbl';
import { errorHandler, successHandler } from '@/utils/toastHandler';

import {
  ActionRow,
  BodyText,
  CheckCircle,
  FeedbackShell,
  InfoGrid,
  InfoLabel,
  InfoValue,
  RatingLabel,
  SectionTitle,
  SubmittedShell,
  SubmittedText,
  SubmittedTitle,
  ValidationText,
} from '../LawyerCaseFeedback/styles';
import { mapPblCaseToOcManagedCase } from '../OcManageCases/caseReviewState';
import { TitleBar } from '../OcManageCases/styles';
import { buildCitizenFeedbackPayload, optionalFeedbackFields } from './feedbackPayload';
import type { CitizenCaseFeedbackForm, OverallRating } from './feedbackPayload';
import { FeedbackTextFields, StarQuestionBlock } from './styles';
import { useTranslation } from 'react-i18next';

type PblCitizenCaseFeedbackProps = {
  caseData?: PblCase;
};

const buildCaseWorkspacePath = (caseId: string | number) => PATH.pbl.caseDetail.replace(':caseId', String(caseId));

const getNumericCaseId = (caseId?: string) => {
  const numericCaseId = Number(caseId);

  return Number.isFinite(numericCaseId) ? numericCaseId : undefined;
};

const getMetadataString = (meeting: PblMeeting | undefined, key: string) => {
  const value = meeting?.metadata?.[key];

  return typeof value === 'string' ? value : undefined;
};

const getMeetingDisplay = (caseData: PblCase | null) => {
  const meeting = caseData?.meetings?.[0];
  const scheduledAt = meeting?.scheduled_at;
  const start = scheduledAt ? dayjs(scheduledAt) : null;

  if (!meeting || !start?.isValid()) {
    return {
      date: '-',
      time: '-',
    };
  }

  const endAt = meeting.ends_at_display || meeting.ends_at || getMetadataString(meeting, 'end_at');
  const end = endAt ? dayjs(endAt) : start.add(45, 'minute');

  return {
    date: start.format('D MMMM, YYYY'),
    time: end.isValid() ? `${start.format('HH:mm')} - ${end.format('HH:mm')}` : start.format('HH:mm'),
  };
};

const PblCitizenCaseFeedback = ({ caseData: caseDataProp }: PblCitizenCaseFeedbackProps) => {
  //   @ts-ignore
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { caseId } = useParams();
  const { isMobile } = useResize();
  const numericCaseId = getNumericCaseId(caseId);
  const [caseData, setCaseData] = useState<PblCase | null>(caseDataProp || null);
  const [isLoadingCase, setIsLoadingCase] = useState(!caseDataProp);
  const [submitted, setSubmitted] = useState(false);
  const fieldAnchorsRef = useRef<Record<string, HTMLElement | null>>({});
  const item = useMemo(() => (caseData ? mapPblCaseToOcManagedCase(caseData) : null), [caseData]);
  const meetingDisplay = useMemo(() => getMeetingDisplay(caseData), [caseData]);
  const {
    control,
    formState: { isSubmitting },
    handleSubmit,
  } = useForm<CitizenCaseFeedbackForm>({
    defaultValues: {
      rating: undefined,
      platform_feedback: '',
      helpful_tools: '',
      other_legal_areas: '',
    },
  });

  const setFieldAnchor = useCallback(
    (fieldName: string) => (element: HTMLElement | null) => {
      fieldAnchorsRef.current[fieldName] = element;
    },
    []
  );

  const scrollToField = useCallback((fieldName: string) => {
    const anchor = fieldAnchorsRef.current[fieldName];
    if (!anchor) return;

    anchor.scrollIntoView({ behavior: 'smooth', block: 'center' });

    const focusTarget = anchor.querySelector<HTMLElement>(
      'input:not([type="hidden"]), textarea, button, [tabindex]:not([tabindex="-1"])'
    );
    focusTarget?.focus({ preventScroll: true });
  }, []);

  const handleInvalidSubmit = useCallback(
    (formErrors: FieldErrors<CitizenCaseFeedbackForm>) => {
      const firstError = (['rating', ...optionalFeedbackFields] as const).find((field) => formErrors[field]);
      if (firstError) scrollToField(firstError);
    },
    [scrollToField]
  );

  useEffect(() => {
    if (caseDataProp) {
      setCaseData(caseDataProp);
      setIsLoadingCase(false);
      return;
    }

    if (!numericCaseId) {
      errorHandler(t('pbl.pagesHardcoded.citizenCaseFeedback.invalidCaseId'));
      setIsLoadingCase(false);
      return;
    }

    let isMounted = true;
    setIsLoadingCase(true);

    getPblCase(numericCaseId)
      .then((response) => {
        if (!isMounted) return;

        if (response.data.success === false) {
          errorHandler(response.data.message || t('pbl.pagesHardcoded.citizenCaseFeedback.failedToLoadCaseDetail'));
          return;
        }

        setCaseData(response.data.data);
      })
      .catch((error: any) => {
        if (!isMounted) return;
        errorHandler(error.response?.data?.message || error.response?.data?.data || t('pbl.pagesHardcoded.citizenCaseFeedback.failedToLoadCaseDetail'));
      })
      .finally(() => {
        if (isMounted) setIsLoadingCase(false);
      });

    return () => {
      isMounted = false;
    };
  }, [caseDataProp, numericCaseId]);

  const resolvedCaseId = caseData?.id || numericCaseId;
  const casePath = resolvedCaseId ? buildCaseWorkspacePath(resolvedCaseId) : PATH.pbl.citizenCases;

  const submitFeedback = async (data: CitizenCaseFeedbackForm) => {
    if (!resolvedCaseId || !data.rating) {
      errorHandler(t('pbl.pagesHardcoded.citizenCaseFeedback.invalidCaseId'));
      return;
    }

    try {
      const response = await submitPblEvaluation(
        resolvedCaseId,
        buildCitizenFeedbackPayload({ ...data, rating: data.rating })
      );

      if (response.data.success === false) {
        errorHandler(response.data.message || t('pbl.pagesHardcoded.citizenCaseFeedback.failedToSubmitFeedback'));
        return;
      }

      try {
        const refreshedResponse = await getPblCase(resolvedCaseId);

        if (refreshedResponse.data.success !== false) {
          setCaseData(refreshedResponse.data.data);
        }
      } catch {
        // Feedback was submitted successfully; a stale case summary should not block the confirmation state.
      }

      successHandler(response.data.message || t('pbl.pagesHardcoded.citizenCaseFeedback.feedbackSubmitted'));
      setSubmitted(true);
    } catch (error: any) {
      errorHandler(error.response?.data?.message || error.response?.data?.data || t('pbl.pagesHardcoded.citizenCaseFeedback.failedToSubmitFeedback'));
    }
  };

  if (submitted && item) {
    return (
      <PageContainer
        title={
          <TitleBar isMobile={isMobile}>
            <Button type="button" size="md" variant="primary" rounded={false} onClick={() => navigate(PATH.pbl.citizenCases)}>
              {t('pbl.pagesHardcoded.citizenCaseFeedback.back')}</Button>
            <span>
              {t('pbl.pagesHardcoded.citizenCaseFeedback.home')}<strong>{t('pbl.pagesHardcoded.citizenCaseFeedback.proBonoLawyersReview')}</strong>
            </span>
          </TitleBar>
        }
      >
        <SubmittedShell>
          <CheckCircle aria-hidden="true">&#10003;</CheckCircle>
          <SubmittedTitle>{t('pbl.pagesHardcoded.citizenCaseFeedback.feedbackSubmitted2')}</SubmittedTitle>
          <SubmittedText>
            {t('pbl.pagesHardcoded.citizenCaseFeedback.thankYouForYourFeedback')}<br />
            {t('pbl.pagesHardcoded.citizenCaseFeedback.yourCaseHasNowBeenClosed')}</SubmittedText>
          <SubmittedText>{t('pbl.pagesHardcoded.citizenCaseFeedback.caseReferenceNo')}{item.caseId}</SubmittedText>
          <Button type="button" size="lg" variant="primary" rounded={false} onClick={() => navigate(PATH.home.index)}>
            {t('pbl.pagesHardcoded.citizenCaseFeedback.returnToDashboard')}</Button>
        </SubmittedShell>
      </PageContainer>
    );
  }

  if (isLoadingCase || !item) {
    return (
      <PageContainer
        title={
          <TitleBar isMobile={isMobile}>
            <Button type="button" size="md" variant="primary" rounded={false} onClick={() => navigate(casePath)}>
              {t('pbl.pagesHardcoded.citizenCaseFeedback.back')}</Button>
            <span>
              {t('pbl.pagesHardcoded.citizenCaseFeedback.home')}<strong>-</strong>
            </span>
          </TitleBar>
        }
      >
        <FeedbackShell as="div">
          <SectionTitle>{t('pbl.pagesHardcoded.citizenCaseFeedback.enquirerFeedbackForm')}</SectionTitle>
          <BodyText>{isLoadingCase ? t('pbl.pagesHardcoded.citizenCaseFeedback.loadingCaseDetail') : t('pbl.pagesHardcoded.citizenCaseFeedback.unableToLoadCaseDetail')}</BodyText>
        </FeedbackShell>
      </PageContainer>
    );
  }

  return (
    <PageContainer
      title={
        <TitleBar isMobile={isMobile}>
          <Button type="button" size="md" variant="primary" rounded={false} onClick={() => navigate(PATH.pbl.citizenCases)}>
            {t('pbl.pagesHardcoded.citizenCaseFeedback.back')}</Button>
          <span>
           <strong>{t('pbl.pagesHardcoded.citizenCaseFeedback.caseRating')}</strong>
          </span>
        </TitleBar>
      }
    >
      <FeedbackShell onSubmit={handleSubmit(submitFeedback, handleInvalidSubmit)}>
        <SectionTitle>{t('pbl.pagesHardcoded.citizenCaseFeedback.enquirerFeedbackForm')}</SectionTitle>
        <BodyText>{t('pbl.pagesHardcoded.citizenCaseFeedback.afterYourConsultationIsCompletePleaseHelpUs')}</BodyText>

        <SectionTitle>{t('pbl.pagesHardcoded.citizenCaseFeedback.districtOfficeStaffInformation')}</SectionTitle>
        <InfoGrid>
          <InfoLabel>{t('pbl.pagesHardcoded.citizenCaseFeedback.legcoMembersOffice')}</InfoLabel>
          <InfoValue>{item.officeName}</InfoValue>
          <InfoLabel>{t('pbl.pagesHardcoded.citizenCaseFeedback.assistingStaffNameLegcoMembersOffice')}</InfoLabel>
          <InfoValue>{item.assistingStaffName}</InfoValue>
          <InfoLabel>{t('pbl.pagesHardcoded.citizenCaseFeedback.officeContact')}</InfoLabel>
          <InfoValue>{item.officeContact}</InfoValue>
        </InfoGrid>

        <SectionTitle>{t('pbl.pagesHardcoded.citizenCaseFeedback.caseInformation')}</SectionTitle>
        <InfoGrid>
          <InfoLabel>{t('pbl.pagesHardcoded.citizenCaseFeedback.caseId')}</InfoLabel>
          <InfoValue>{item.caseId}</InfoValue>
          <InfoLabel>{t('pbl.pagesHardcoded.citizenCaseFeedback.enquirer')}</InfoLabel>
          <InfoValue>{item.enquirer.fullName}</InfoValue>
          <InfoLabel>{t('pbl.pagesHardcoded.citizenCaseFeedback.hkid4Digits')}</InfoLabel>
          <InfoValue>{item.enquirer.identityPartial}</InfoValue>
        </InfoGrid>

        <SectionTitle>{t('pbl.pagesHardcoded.citizenCaseFeedback.detailsOfThe45MinuteProBonoLegal')}</SectionTitle>
        <InfoGrid>
          <InfoLabel>{t('pbl.pagesHardcoded.citizenCaseFeedback.date')}</InfoLabel>
          <InfoValue>{meetingDisplay.date}</InfoValue>
          <InfoLabel>{t('pbl.pagesHardcoded.citizenCaseFeedback.time')}</InfoLabel>
          <InfoValue>{meetingDisplay.time}</InfoValue>
        </InfoGrid>

        <SectionTitle>{t('pbl.pagesHardcoded.citizenCaseFeedback.overallRating')}</SectionTitle>
        <BodyText>{t('pbl.pagesHardcoded.citizenCaseFeedback.textExcellent4VeryGood3Good2')}</BodyText>
        <StarQuestionBlock ref={setFieldAnchor('rating')}>
          <RatingLabel>{t('pbl.pagesHardcoded.citizenCaseFeedback.overallRating2')}</RatingLabel>
          <Controller
            control={control}
            name="rating"
            rules={{ required: t('pbl.pagesHardcoded.citizenCaseFeedback.selectAnOverallRating') }}
            render={({ field, fieldState }) => (
              <>
                <StarRating
                  label={t('pbl.pagesHardcoded.citizenCaseFeedback.overallRating')}
                  value={field.value}
                  onChange={(value) => field.onChange(value as OverallRating)}
                />
                {fieldState.error?.message && <ValidationText>{fieldState.error.message}</ValidationText>}
              </>
            )}
          />
        </StarQuestionBlock>

        <SectionTitle>{t('pbl.pagesHardcoded.citizenCaseFeedback.additionalFeedback')}</SectionTitle>
        <FeedbackTextFields>
          {optionalFeedbackFields.map((fieldName) => (
            <StarQuestionBlock key={fieldName} ref={setFieldAnchor(fieldName)}>
              <Controller
                control={control}
                name={fieldName}
                rules={{
                  maxLength: {
                    value: 2000,
                    message: t('pbl.pagesHardcoded.citizenCaseFeedback.answerTooLong'),
                  },
                }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    label={t(`pbl.pagesHardcoded.citizenCaseFeedback.${fieldName}`)}
                    variant="standard"
                    multiline
                    minRows={2}
                    inputProps={{ maxLength: 2000 }}
                    error={Boolean(fieldState.error)}
                    helperText={fieldState.error?.message}
                    fullWidth
                  />
                )}
              />
            </StarQuestionBlock>
          ))}
        </FeedbackTextFields>

        <ActionRow>
          <Button type="submit" size="lg" variant="primary" rounded={false} disabled={isSubmitting}>
            {isSubmitting ? t('pbl.pagesHardcoded.citizenCaseFeedback.submitting') : t('pbl.pagesHardcoded.citizenCaseFeedback.submitFeedback')}
          </Button>
          <Button
            type="button"
            size="lg"
            variant="primary-invert"
            rounded={false}
            onClick={() => navigate(PATH.pbl.citizenCases)}
          >
            {t('pbl.pagesHardcoded.citizenCaseFeedback.cancel')}</Button>
        </ActionRow>
      </FeedbackShell>
    </PageContainer>
  );
};

export default PblCitizenCaseFeedback;
